import React, { useState } from 'react';
import {
  Box, VStack, HStack, Text, Heading, Badge, Button,
  Container, Flex, Icon, Table, Thead, Tbody, Tr, Th, Td,
  Divider, useToast,
} from '@chakra-ui/react';
import {
  FaCreditCard, FaCheckCircle, FaLock, FaVial,
  FaSyringe, FaUserMd, FaCalendarAlt, FaReceipt,
  FaHospital, FaPhoneAlt,
} from 'react-icons/fa';

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Mock data
const MOCK_ORDER = {
  submission_id: 'demo-001',
  patient_display_name: 'J. Smith',
  patient_email_masked: 'j***@email.com',
  doctor_name: 'Dr. Sarah Chen',
  lab_name: 'Quest Diagnostics',
  company_name: 'AtHome Mobile Phlebotomy',
  phlebotomist_name: 'Maria Garcia',
  is_self_pay: true,
  payment_status: 'unpaid',
  submitted_at: '2026-03-20T10:00:00Z',
  collection_date: '2026-03-22',
  draw_fee: 25.00,
  tests: [
    { name: 'CBC (Complete Blood Count)', price: 15.00 },
    { name: 'Comprehensive Metabolic Panel', price: 35.00 },
    { name: 'Lipid Panel', price: 20.00 },
    { name: 'Thyroid Panel (TSH, Free T4)', price: 30.00 },
  ],
  test_total: 100.00,
  total_amount: 125.00,
};

const PatientPaymentPreview = () => {
  const [order] = useState(MOCK_ORDER);
  const [paymentStatus, setPaymentStatus] = useState(order.payment_status);
  const toast = useToast();

  const isPaid = paymentStatus === 'paid';

  const handlePayNow = () => {
    toast({
      title: 'Demo Mode',
      description: 'In production, this redirects to Stripe Checkout.',
      status: 'info',
      duration: 3000,
    });
  };

  const handleMarkPaid = () => {
    setPaymentStatus('paid');
    toast({
      title: 'Payment Successful!',
      description: 'Your payment has been processed.',
      status: 'success',
      duration: 3000,
    });
  };

  return (
    <Box bg="gray.50" minH="100vh">
      {/* Preview Banner */}
      <Box bg="purple.500" py={2} textAlign="center">
        <Text color="white" fontSize="sm" fontWeight="700" letterSpacing="wide">
          PREVIEW MODE — This is a demo with mock data
        </Text>
      </Box>

      <Container maxW="700px" py={8} px={4}>
        {/* Header */}
        <Box bg="teal.600" borderRadius="2xl" p={6} mb={6} color="white" position="relative" overflow="hidden">
          <Box position="absolute" top={-4} right={-4} opacity={0.1}>
            <Icon as={FaSyringe} boxSize={32} />
          </Box>
          <VStack spacing={1} align="start">
            <Text fontSize="sm" fontWeight="600" opacity={0.9} letterSpacing="wide">
              AtHomeBloodDraw.com
            </Text>
            <Heading size="lg" fontWeight="800">
              BLOOD DRAW PAYMENT
            </Heading>
            <Text fontSize="sm" opacity={0.8}>
              Mobile Phlebotomy Services
            </Text>
          </VStack>
          <Flex mt={4} justify="space-between" align="center">
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" opacity={0.7} textTransform="uppercase">Patient</Text>
              <Text fontSize="md" fontWeight="700">{order.patient_display_name}</Text>
            </VStack>
            <VStack align="end" spacing={0}>
              <Text fontSize="xs" opacity={0.7} textTransform="uppercase">Order Date</Text>
              <Text fontSize="md" fontWeight="700">{formatDate(order.submitted_at)}</Text>
            </VStack>
          </Flex>
        </Box>

        {/* Status Badge */}
        <Flex justify="center" mb={6}>
          <Badge
            colorScheme={isPaid ? 'green' : 'orange'}
            fontSize="md"
            px={6}
            py={2}
            borderRadius="full"
            fontWeight="700"
          >
            <HStack spacing={2}>
              <Icon as={isPaid ? FaCheckCircle : FaReceipt} />
              <Text>{isPaid ? 'PAID' : 'PAYMENT DUE'}</Text>
            </HStack>
          </Badge>
        </Flex>

        {/* Order Details Card */}
        <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
          <Box px={6} py={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
            <HStack spacing={2}>
              <Icon as={FaVial} color="teal.500" />
              <Text fontSize="md" fontWeight="700" color="gray.700">Order Details</Text>
            </HStack>
          </Box>
          <Box px={6} py={4}>
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Icon as={FaUserMd} color="gray.400" boxSize={3} />
                  <Text fontSize="sm" color="gray.500">Ordering Physician</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="600">{order.doctor_name}</Text>
              </HStack>
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Icon as={FaHospital} color="gray.400" boxSize={3} />
                  <Text fontSize="sm" color="gray.500">Laboratory</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="600">{order.lab_name}</Text>
              </HStack>
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Icon as={FaPhoneAlt} color="gray.400" boxSize={3} />
                  <Text fontSize="sm" color="gray.500">Phlebotomist</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="600">{order.phlebotomist_name}</Text>
              </HStack>
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Icon as={FaCalendarAlt} color="gray.400" boxSize={3} />
                  <Text fontSize="sm" color="gray.500">Collection Date</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="600">{formatDate(order.collection_date)}</Text>
              </HStack>
            </VStack>
          </Box>
        </Box>

        {/* Charges Breakdown */}
        <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
          <Box px={6} py={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
            <HStack spacing={2}>
              <Icon as={FaReceipt} color="teal.500" />
              <Text fontSize="md" fontWeight="700" color="gray.700">Charges</Text>
            </HStack>
          </Box>
          <Box px={6} py={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th px={0} fontSize="xs" color="gray.500">Description</Th>
                  <Th px={0} isNumeric fontSize="xs" color="gray.500">Amount</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td px={0} py={3} borderColor="gray.100">
                    <HStack spacing={2}>
                      <Icon as={FaSyringe} color="teal.400" boxSize={3} />
                      <Text fontSize="sm" fontWeight="600">Blood Draw Service Fee</Text>
                    </HStack>
                  </Td>
                  <Td px={0} py={3} isNumeric borderColor="gray.100">
                    <Text fontSize="sm" fontWeight="600">{formatCurrency(order.draw_fee)}</Text>
                  </Td>
                </Tr>
                {order.tests.map((test, i) => (
                  <Tr key={i}>
                    <Td px={0} py={3} borderColor="gray.100">
                      <HStack spacing={2}>
                        <Icon as={FaVial} color="blue.400" boxSize={3} />
                        <Text fontSize="sm">{test.name}</Text>
                      </HStack>
                    </Td>
                    <Td px={0} py={3} isNumeric borderColor="gray.100">
                      <Text fontSize="sm">{formatCurrency(test.price)}</Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Divider my={3} />
            <Flex justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="800" color="gray.800">Total Due</Text>
              <Text fontSize="2xl" fontWeight="800" color={isPaid ? 'green.500' : 'teal.600'}>
                {formatCurrency(order.total_amount)}
              </Text>
            </Flex>
          </Box>
        </Box>

        {/* Payment Section */}
        {!isPaid ? (
          <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
            <Box px={6} py={4} bg="teal.50" borderBottom="1px solid" borderColor="teal.100">
              <HStack spacing={2}>
                <Icon as={FaCreditCard} color="teal.600" />
                <Text fontSize="md" fontWeight="700" color="teal.700">Pay Now</Text>
              </HStack>
            </Box>
            <Box px={6} py={5}>
              <VStack spacing={4}>
                <Button
                  colorScheme="teal"
                  size="lg"
                  w="full"
                  leftIcon={<FaCreditCard />}
                  onClick={handlePayNow}
                  fontWeight="700"
                  fontSize="md"
                  py={7}
                  borderRadius="xl"
                  boxShadow="md"
                >
                  Pay {formatCurrency(order.total_amount)} with Card
                </Button>
                <HStack spacing={1} justify="center">
                  <Icon as={FaLock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.400">Secured by Stripe — 256-bit SSL encryption</Text>
                </HStack>
              </VStack>
            </Box>
          </Box>
        ) : (
          <Box bg="green.50" borderRadius="2xl" boxShadow="md" border="2px solid" borderColor="green.200" mb={6} p={6} textAlign="center">
            <Icon as={FaCheckCircle} color="green.500" boxSize={12} mb={3} />
            <Heading size="md" color="green.700" mb={2}>Payment Complete</Heading>
            <Text fontSize="sm" color="green.600">
              Thank you! Your payment of {formatCurrency(order.total_amount)} has been received.
            </Text>
            <Text fontSize="xs" color="green.500" mt={2}>
              A receipt has been sent to {order.patient_email_masked}
            </Text>
          </Box>
        )}

        {/* Demo Controls */}
        {!isPaid && (
          <Flex justify="center" mb={4}>
            <Button size="sm" colorScheme="green" variant="outline" onClick={handleMarkPaid}>
              Demo: Simulate Successful Payment
            </Button>
          </Flex>
        )}

        {/* Footer */}
        <VStack spacing={2} mt={8}>
          <Text fontSize="xs" color="gray.400" textAlign="center">
            AtHomeBloodDraw.com — Mobile Phlebotomy Services
          </Text>
          <Text fontSize="xs" color="gray.400" textAlign="center">
            Questions? Contact us at support@athomeblooddraw.com
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default PatientPaymentPreview;
